import { useState, useEffect, useId } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, Clock, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function CompletionModal() {
  const { pendingCompletion, tasks, executeCompletion, resetCompletionPreference } = useAppStore()
  const [remember, setRemember] = useState(false)
  const uid = useId()
  const titleId = `completion-title-${uid}`

  const task = pendingCompletion ? tasks.find((t) => t.id === pendingCompletion.taskId) : null

  function choose(destination) {
    executeCompletion(pendingCompletion.taskId, destination, pendingCompletion.origin, remember)
    setRemember(false)
  }

  useEffect(() => {
    if (!pendingCompletion) return
    const handler = (e) => { if (e.key === 'Escape') dismiss() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [pendingCompletion])

  function dismiss() {
    useAppStore.getState().set?.({ pendingCompletion: null })
    // Use the store directly
    useAppStore.setState({ pendingCompletion: null })
    setRemember(false)
  }

  return (
    <AnimatePresence>
      {pendingCompletion && task && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[3px] z-[200]"
            onClick={dismiss}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            key="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 8 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[201] w-full max-w-[400px] px-4"
          >
            <div className="bg-surface-raised border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/60 overflow-hidden">
              {/* Header */}
              <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35 mb-1">Completing task</p>
                  <p id={titleId} className="text-sm font-semibold text-white leading-snug line-clamp-2">{task.title}</p>
                </div>
                <button onClick={dismiss} aria-label="Close dialog" className="mt-0.5 w-6 h-6 flex items-center justify-center rounded-md text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors flex-shrink-0">
                  <X size={13} aria-hidden="true" />
                </button>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/[0.05] mx-5" />

              {/* Choices */}
              <div className="p-4 flex gap-3">
                {/* Approval */}
                <button
                  onClick={() => choose('approval')}
                  className="flex-1 flex flex-col items-center gap-2.5 px-3 py-4 rounded-xl border border-white/[0.08] hover:border-[#ff9500]/40 hover:bg-[#ff9500]/[0.06] transition-all active:scale-[0.97] group"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#ff9500]/[0.12] group-hover:bg-[#ff9500]/[0.18] transition-colors">
                    <Clock size={18} className="text-[#ff9500]" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold text-white">Send for Approval</p>
                    <p className="text-[10px] text-white/40 mt-0.5 leading-tight">Requires sign-off before closing</p>
                  </div>
                </button>

                {/* Complete */}
                <button
                  onClick={() => choose('complete')}
                  className="flex-1 flex flex-col items-center gap-2.5 px-3 py-4 rounded-xl border border-white/[0.08] hover:border-[#34d399]/40 hover:bg-[#34d399]/[0.06] transition-all active:scale-[0.97] group"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#34d399]/[0.12] group-hover:bg-[#34d399]/[0.18] transition-colors">
                    <CheckCircle size={18} className="text-[#34d399]" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold text-white">Mark Complete</p>
                    <p className="text-[10px] text-white/40 mt-0.5 leading-tight">Done — move to completed</p>
                  </div>
                </button>
              </div>

              {/* Remember + footer */}
              <div className="px-4 pb-4 flex items-center justify-between">
                <button
                  onClick={() => setRemember((r) => !r)}
                  className="flex items-center gap-2 group"
                >
                  <div className={cn(
                    'w-4 h-4 rounded border flex items-center justify-center transition-all',
                    remember ? 'bg-accent border-accent' : 'border-white/25 group-hover:border-white/40'
                  )}>
                    {remember && (
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <path d="M1 4l2 2 4-4" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span className="text-[11px] text-white/45 group-hover:text-white/65 transition-colors">Remember my choice</span>
                </button>
                <button
                  onClick={() => choose('none')}
                  className="text-[11px] text-white/30 hover:text-white/55 transition-colors"
                >
                  Just complete
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
